import { Link } from "react-router";

import { Blocks } from "@/components/cms/Blocks";
import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import { chosenFocalPoint, linkSlot, visibleItems } from "@/components/cms/SectionRenderer";
import { ArrowRight, ButtonLink, TextLink } from "@/components/ui/Button";
import { ImpactCounter } from "@/components/ui/ImpactCounter";
import { MediaFigure } from "@/components/ui/MediaFigure";
import { MediaImage } from "@/components/ui/MediaImage";
import { PageHeader } from "@/components/ui/PageHeader";
import { PartnerStrip } from "@/components/ui/PartnerStrip";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { useContent, useMediaByKey, usePillars, useSettings } from "@/content-context";
import type { MediaItem } from "@/data/media";
import { volunteering } from "@/data/media";
import { locations } from "@/data/locations";
import { buildMetadata } from "@/lib/seo";
import { imageReveal } from "@/lib/motion";
import { pageLoader, usePageSection } from "@/page-data";

/**
 * About.
 *
 * Rebuilt around what an institutional reader actually looks for, in the order
 * they look for it: who this is, where it came from, what it is trying to do,
 * how it works, what it has done, where, what it can evidence, who runs it,
 * what it is registered as, who else works with it, and how to start a
 * conversation. The information architecture follows the pattern shared by
 * charity: water, CARE, BRAC, Room to Read and WaterAid; none of the content is
 * theirs.
 *
 * Every section on this page is editable and reorderable in the page editor.
 * Leadership stays hidden while HISCUS has published no names: a card explaining
 * that the list is pending tells a visitor about our content process rather
 * than about HISCUS.
 */

export const loader = pageLoader("about");

export const meta = () =>
  buildMetadata({
    title: "About HISCUS",
    description:
      "HISCUS Nepal was established in 1998. Origin, mission, vision, values, programme areas, registration and governance.",
    path: "/about",
  });

/* 1. Overview */
function IntroSection({ section }: SectionProps) {
  const settings = useSettings();
  const byKey = useMediaByKey();
  const pillars = usePillars();
  const image = byKey(section.mediaKey) ?? volunteering[0];

  return (
    <Section>
      <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal variants={imageReveal}>
          <MediaFigure
            item={image}
            ratio="4 / 3"
            sizes="(min-width: 1024px) 50vw, 100vw"
            objectPosition={chosenFocalPoint(section.settings?.focalPoint)}
          />
        </Reveal>

        <Reveal>
          <h2 style={{ fontSize: "var(--step-h2)" }}>{section.title || "Who we are"}</h2>
          <Blocks blocks={section.body} className="mt-4" />
          {settings.motto ? <p className="mt-6 font-serif text-[1.375rem] text-navy">{settings.motto}</p> : null}

          <dl className="mt-8 grid gap-4 sm:grid-cols-3">
            <Stat label="Established" value={String(settings.establishedYear ?? 1998)} />
            <Stat label="Programme areas" value={String(pillars.length)} />
            <Stat label="Documented communities" value={String(locations.length)} />
          </dl>
        </Reveal>
      </div>
    </Section>
  );
}

/* 2. Origin */
function OriginSection({ section }: SectionProps) {
  return (
    <Section tone="ivory" ariaLabelledBy="origin-heading" compact>
      <div className="grid items-start gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-16">
        <Reveal>
          <SectionHeading id="origin-heading" eyebrow={section.eyebrow} title={section.title || "How HISCUS started"} />
        </Reveal>
        <Reveal>
          <Blocks blocks={section.body} />
        </Reveal>
      </div>
    </Section>
  );
}

/* 3 and 4. Vision and mission, in the organisation's own words from its profile. */
function MissionSection() {
  const settings = useSettings();
  if (!settings.vision && !settings.mission) return null;
  return (
    <Section tone="navy" ariaLabelledBy="vision-heading" compact>
      <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
        {settings.vision ? (
          <Reveal>
            <h2 id="vision-heading" className="text-white" style={{ fontSize: "var(--step-h3)" }}>
              Vision
            </h2>
            <p className="mt-4 text-sky" style={{ fontSize: "var(--step-lead)" }}>
              {settings.vision}
            </p>
          </Reveal>
        ) : null}
        {settings.mission ? (
          <Reveal>
            <h2 className="text-white" style={{ fontSize: "var(--step-h3)" }}>
              Mission
            </h2>
            <p className="mt-4 text-sky" style={{ fontSize: "var(--step-lead)" }}>
              {settings.mission}
            </p>
          </Reveal>
        ) : null}
      </div>
    </Section>
  );
}

/* 5 and 6. Values, which are also how HISCUS works. */
function PrinciplesSection({ section }: SectionProps) {
  const settings = useSettings();
  const values = (settings.values ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  if (!values.length) return null;
  return (
    <Section ariaLabelledBy="values-heading" compact>
      <SectionHeading
        id="values-heading"
        eyebrow={section.eyebrow}
        title={section.title || `${numberWord(values.length)} working principles`}
      />
      <RevealGroup as="ul" className="mt-8 grid gap-4 md:grid-cols-2">
        {values.map((value) => (
          <Reveal as="li" key={value.title}>
            <div className="surface h-full rounded-[16px] p-5 md:p-6">
              <h3 style={{ fontSize: "var(--step-h4)" }}>{value.title}</h3>
              <p className="mt-2.5 text-muted">{value.body}</p>
            </div>
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

/* 7. Programme areas, and the places beneath them. */
function ProgrammesSection({ section }: SectionProps) {
  const pillars = usePillars();
  const all = linkSlot(section, 0);
  const places = linkSlot(section, 1);

  return (
    <Section tone="mist" ariaLabelledBy="programmes-heading" compact>
      <div className="flex flex-wrap items-end justify-between gap-8">
        <SectionHeading
          id="programmes-heading"
          eyebrow={section.eyebrow}
          title={section.title || "Four programme areas"}
          lead={section.lead}
        />
        {all ? <TextLink href={all.href}>{all.label}</TextLink> : null}
      </div>
      <RevealGroup as="ul" className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {pillars.map((pillar) => (
          <Reveal as="li" key={pillar.slug}>
            <Link
              to={`/our-work/${pillar.slug}`}
              className="surface group relative flex h-full flex-col overflow-hidden rounded-[16px]"
            >
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-mist">
                <MediaImage
                  item={pillar.cardImage ?? pillar.image}
                  fill
                  sizes="(min-width: 1024px) 23vw, (min-width: 640px) 46vw, 100vw"
                  className="object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
                />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 style={{ fontSize: "var(--step-h4)" }}>{pillar.shortTitle}</h3>
                <p className="mt-2 text-[0.875rem] leading-relaxed text-muted">{pillar.outcome}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </RevealGroup>

      {/*
        The seven places, under the four programmes rather than in a section
        of their own. A list of seven names does not need a full band, and it
        reads better as the answer to "where" directly beneath "what".
      */}
      <Reveal className="mt-10 border-t border-line-strong/60 pt-8">
        <div className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-3">
          <h3 className="text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-blue">
            {section.texts?.placesHeading || "Communities with dated work in HISCUS reporting"}
          </h3>
          {places ? <TextLink href={places.href}>{places.label}</TextLink> : null}
        </div>
        <ul className="mt-5 flex flex-wrap gap-x-7 gap-y-2">
          {locations.map((place) => (
            <li key={place.id}>
              <Link
                to={`/where-we-work#${place.id}`}
                className="font-serif text-[1.25rem] text-midnight transition-colors duration-300 hover:text-navy"
              >
                {place.name}
              </Link>
            </li>
          ))}
        </ul>
      </Reveal>
    </Section>
  );
}

/* 9. Timeline, with the photographs that illustrate it. */
function TimelineSection({ section }: SectionProps) {
  const byKey = useMediaByKey();
  const photos = (section.mediaKeys ?? []).map((key) => byKey(key)).filter((item): item is MediaItem => Boolean(item));

  return (
    <Section tone="ivory" ariaLabelledBy="timeline-heading" compact>
      <SectionHeading
        id="timeline-heading"
        eyebrow={section.eyebrow}
        title={section.title || "Documented milestones"}
        lead={section.lead}
      />

      <RevealGroup as="ol" className="mt-10 space-y-0">
        {visibleItems(section).map((entry) => (
          <Reveal as="li" key={(entry.date ?? "") + (entry.title ?? "")}>
            <div className="grid gap-3 border-l-2 border-line pb-8 pl-6 last:pb-0 sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-8 sm:pl-8">
              <p className="font-serif text-[1.75rem] leading-none text-blue">{entry.date}</p>
              <div>
                <h3 style={{ fontSize: "var(--step-h4)" }}>{entry.title}</h3>
                {entry.text ? <p className="mt-2 text-muted">{entry.text}</p> : null}
              </div>
            </div>
          </Reveal>
        ))}
      </RevealGroup>

      {photos.length ? (
        <RevealGroup as="ul" className="mt-10 grid gap-5 sm:grid-cols-3">
          {photos.map((item) => (
            <Reveal as="li" key={item.id}>
              <MediaFigure item={item} ratio="4 / 3" sizes="(min-width: 640px) 31vw, 100vw" />
            </Reveal>
          ))}
        </RevealGroup>
      ) : null}
    </Section>
  );
}

/* 10. Verified impact snapshot */
function EvidenceSection({ section }: SectionProps) {
  const { impact } = useContent();
  const record = linkSlot(section, 0);
  if (!impact.length) return null;
  return (
    <Section ariaLabelledBy="impact-heading" compact>
      <div className="flex flex-wrap items-end justify-between gap-8">
        <SectionHeading
          id="impact-heading"
          eyebrow={section.eyebrow}
          title={section.title || "What the record shows"}
          lead={section.lead}
        />
        {record ? <TextLink href={record.href}>{record.label}</TextLink> : null}
      </div>
      <RevealGroup as="ul" className="mt-9 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
        {impact.map((metric) => (
          <Reveal as="li" key={metric.key}>
            <ImpactCounter
              value={metric.value}
              suffix={metric.suffix ?? undefined}
              label={metric.label}
              context={metric.context}
              asOf={metric.asOf}
            />
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

/* 11, 12, 13. Registration, governance, partners */
function RegistrationSection({ section }: SectionProps) {
  const settings = useSettings();
  const governance = usePageSection("about.governance");
  const people = visibleItems(governance);
  const button = linkSlot(section, 0);

  return (
    <Section ariaLabelledBy="registration-heading" compact>
      <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <SectionHeading
            id="registration-heading"
            eyebrow={section.eyebrow}
            title={section.title || "Registration and affiliation"}
          />
          <dl className="mt-8 space-y-3.5">
            <Row label="Registered name" value={settings.legalName} />
            <Row label="Short name" value={settings.shortName} />
            <Row label="Established" value={String(settings.establishedYear ?? 1998)} />
            {settings.address.confirmed && settings.address.value ? (
              <Row label="Registered address" value={settings.address.value} />
            ) : null}
            {settings.registrations.map((reg) => (
              <Row key={reg.value} label={reg.label} value={reg.value} />
            ))}
          </dl>

          {button ? (
            <div className="mt-8 flex flex-wrap gap-3">
              <ButtonLink href={button.href} variant="secondary">
                {button.label}
                <ArrowRight />
              </ButtonLink>
            </div>
          ) : null}
        </Reveal>

        <Reveal>
          <div id="governance">
            {/*
              Leadership and governance renders only when there is somebody to
              name. Nothing about the organisation's own process belongs on a
              page a funder is reading, and a placeholder person would be a
              fabrication. Tracked in CLIENT_CONTENT_REQUIRED.md.
            */}
            {governance && people.length ? (
              <>
                <SectionHeading
                  eyebrow={governance.eyebrow}
                  title={governance.title || "Organisation and governance"}
                  lead={governance.lead}
                  level={3}
                />
                <ul className="mt-8 grid gap-5 sm:grid-cols-2">
                  {people.map((person) => (
                    <li key={person.title} className="surface rounded-[16px] p-5">
                      <h4 style={{ fontSize: "var(--step-h4)" }}>{person.title}</h4>
                      {person.label ? <p className="mt-1 text-[0.9375rem] text-blue">{person.label}</p> : null}
                      {person.text ? <p className="mt-2.5 text-[0.9375rem] text-muted">{person.text}</p> : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            <div className={governance && people.length ? "mt-10" : ""}>
              <PartnerStrip />
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/** Governance appears beside Registration, so on its own it draws nothing. */
function GovernanceSection() {
  return null;
}

/* 14 and 15. Accountability, reports and policies */
function AccountabilitySection({ section }: SectionProps) {
  const { documents } = useContent();
  const request = linkSlot(section, 0);

  return (
    <Section tone="ivory" ariaLabelledBy="accountability-heading" compact>
      <div className="grid items-start gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:gap-16">
        <Reveal>
          <SectionHeading
            id="accountability-heading"
            eyebrow={section.eyebrow}
            title={section.title || "What HISCUS publishes"}
          />
          <Blocks blocks={section.body} className="mt-6" />
          {request ? (
            <div className="mt-7">
              <TextLink href={request.href}>{request.label}</TextLink>
            </div>
          ) : null}
        </Reveal>

        <Reveal>
          <ul className="grid gap-4 sm:grid-cols-2">
            {documents.length ? (
              documents.map((doc) => (
                <li key={doc.title} className="surface rounded-[16px] p-5">
                  <p className="eyebrow">{doc.category.replace(/-/g, " ")}</p>
                  <h3 className="mt-2.5" style={{ fontSize: "var(--step-h4)" }}>
                    {doc.title}
                  </h3>
                  {doc.period ? <p className="mt-1 text-[0.875rem] text-muted">{doc.period}</p> : null}
                  {doc.availability === "download" && doc.fileUrl ? (
                    <a
                      href={doc.fileUrl}
                      className="mt-3 inline-flex min-h-11 items-center font-semibold text-navy underline underline-offset-4"
                    >
                      Download
                    </a>
                  ) : (
                    <p className="mt-3 text-[0.875rem] text-muted">Available on request.</p>
                  )}
                </li>
              ))
            ) : (
              <li className="surface rounded-[16px] p-6 sm:col-span-2">
                <h3 style={{ fontSize: "var(--step-h4)" }}>No documents published yet</h3>
                <p className="mt-2.5 text-muted">
                  Registration references are published on the Transparency page. Reports,
                  financials and approved policies appear here as they are cleared for release.
                </p>
                <p className="mt-4">
                  <Link to="/privacy" className="link-underline font-semibold text-navy">
                    Privacy
                  </Link>
                  {" · "}
                  <Link to="/terms" className="link-underline font-semibold text-navy">
                    Terms and conditions
                  </Link>
                  {" · "}
                  <Link to="/safeguarding" className="link-underline font-semibold text-navy">
                    Safeguarding
                  </Link>
                </p>
              </li>
            )}
          </ul>
        </Reveal>
      </div>
    </Section>
  );
}

/* 16 and 17. The two calls to action. */
function ClosingSection({ section }: SectionProps) {
  const primary = linkSlot(section, 0);
  const secondary = linkSlot(section, 1);
  return (
    <Section tone="midnight" ariaLabelledBy="about-cta-heading">
      <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:gap-16">
        <Reveal>
          <h2 id="about-cta-heading" className="text-white" style={{ fontSize: "var(--step-h2)" }}>
            {section.title}
          </h2>
        </Reveal>
        <Reveal className="flex flex-col gap-3 sm:flex-row lg:justify-end">
          {primary ? (
            <ButtonLink href={primary.href} variant="light">
              {primary.label}
              <ArrowRight />
            </ButtonLink>
          ) : null}
          {secondary ? (
            <ButtonLink href={secondary.href} variant="ghostLight">
              {secondary.label}
            </ButtonLink>
          ) : null}
        </Reveal>
      </div>
    </Section>
  );
}

export const aboutSections: SectionRenderers = {
  "about.intro": IntroSection,
  "about.origin": OriginSection,
  "about.mission": MissionSection,
  "about.how-we-work": PrinciplesSection,
  "about.programmes": ProgrammesSection,
  "about.timeline": TimelineSection,
  "about.evidence": EvidenceSection,
  "about.registration": RegistrationSection,
  "about.governance": GovernanceSection,
  "about.accountability": AccountabilitySection,
  "about.closing": ClosingSection,
};

export function AboutPage() {
  const settings = useSettings();
  return (
    <>
      <PageHeader
        eyebrow="About"
        title="A Nepali organisation, working in Nepali communities since 1998"
        lead={settings.positioning}
        trail={[
          { name: "Home", href: "/" },
          { name: "About", href: "/about" },
        ]}
        actions={
          <div className="flex flex-col gap-3 sm:flex-row">
            <ButtonLink href="/get-involved/support">
              Support our work
              <ArrowRight />
            </ButtonLink>
            <ButtonLink href="/contact" variant="secondary">
              Contact HISCUS
            </ButtonLink>
          </div>
        }
      />
      <PageSections renderers={aboutSections} />
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap justify-between gap-x-6 gap-y-1 border-b border-line pb-3.5 last:border-0">
      <dt className="text-muted">{label}</dt>
      <dd className="font-semibold text-ink">{value}</dd>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[14px] border border-line bg-mist p-4">
      <dt className="text-[0.8125rem] leading-snug text-muted">{label}</dt>
      <dd className="mt-1 font-serif text-[1.375rem] text-midnight">{value}</dd>
    </div>
  );
}

const WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight"];
const numberWord = (n: number) => WORDS[n] ?? String(n);
